import moment from 'moment'
import { BrowserTracker, SelfDescribingJson } from '@snowplow/browser-tracker'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import jwt_decode, { JwtPayload } from 'jwt-decode'

import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getCookies, setCookie } from 'lib/web/cookieUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { decodeCookie } from 'lib/web/cookieUtils'
import { getOfferById } from 'api/offer'
import { getExperienceById } from 'api/experiences'
import { PROVIDERS } from 'constants/experience'
import { OfferContextEvent } from './context'
import { getCruiseOffer } from 'api/cruises'
import { dateDifference } from 'lib/datetime/dateUtils'
import { removeUndefinedAndNullProperties } from 'lib/objectUtils'

/**
 * Event definition helpers
 **/
export function buildInsuranceUpsellContext(
  notSuitable: string,
  yes: string,
  no: string,
  whyNotSuitable?: string,
  isCovermore?: string,
  isOffered?: string,
  declineReason?: string,
): {
  schema: string;
  data: Record<string, unknown>;
}[] {
  const token = decodeCookie('access_token')
  const decodedToken = jwt_decode(token) as JwtPayload
  const agentId = decodedToken?.aud as string

  const data = [
    {
      agentId,
      questionId: 'isCovermore',
      questionText:
            'Is the customer purchasing insurance through Covermore?',
      answer: isCovermore,
    },
    ...(isCovermore === no ?
        [
          {
            agentId,
            questionId: 'isOffered',
            questionText: 'Did you offer insurance to the customer?',
            answer: isOffered,
          },
        ] :
        []),
    ...(isOffered === notSuitable && whyNotSuitable !== '' ?
        [
          {
            agentId,
            questionId: 'whyNotSuitable',
            questionText: 'Why was the customer/product not suitable?',
            answer: whyNotSuitable,
          },
        ] :
        []),
    ...(isOffered === yes && declineReason !== '' ?
        [
          {
            agentId,
            questionId: 'declineReason',
            questionText: 'Why did the customer decline the insurance?',
            answer: declineReason,
          },
        ] :
        []),
  ]

  return data.map((item) => comluxgroup.createSurvey_1_0_0(item))
}

export interface CartExtras {
  paymentType?: string | undefined;
  errorReason?: KnownPaymentErrorReasons;
}

export type KnownPaymentErrorReasons =
  'accountTakeover' |
  'bedbankPriceChanged' |
  'bookingDateWarning' |
  'bridgerFailed' |
  'cantProcessOnlineOrder' |
  'flightsSessionExpired' |
  'generic' |
  'journeyPriceChanged' |
  'journeySoldOut' |
  'latitudeFailed' |
  'tourV2PriceChanged' |
  'travellerNameTooLong'

export type CartTrackingEvent =
    'ADD_INSURANCE_PRESSED' |
    'DONT_ADD_INSURANCE_PRESSED' |
    'FLIGHT_SELECT_PRESSED' |
    'TRANSACTION_FAILED' |
    'TRANSACTION_STARTED' |
    'TRANSACTION_SUCCEEDED' |
    'DEPARTING_FLIGHT_PAGE_VIEWED' |
    'RETURN_FLIGHT_PAGE_VIEWED' |
    'CONTINUE_TO_EXPERIENCE_UPSELL_PRESSED' |
    'EXPERIENCE_PAGE_VIEWED' |
    'CONTINUE_TO_CHECKOUT_PRESSED' |
    'SKIP_EXPERIENCE_UPSELL' |
    'REVIEW_PAYMENT_PAGE_VIEWED' |
    'PAYMENT_METHOD_PRESSED' |
    'PAY_PRESSED' |
    'CONFIRM_TRAVELLER_DETAILS_MODAL_VIEWED' |
    'CONFIRM_TRAVELLER_DETAILS_PRESSED' |
    'BOOKING_CONFIRMED_VIEWED' |
    'VIEW_RETURN_FLIGHTS_PRESSED'

const CART_CATEGORIES = {
  InflowDepartingFlight: 'inflow_departing_flight',
  InflowExperiencesPage: 'inflow_experiences_page',
  ReviewPayment: 'review_payment',
  BookingConfirmed: 'booking_confirmed',
}

export const cartEventsMap: Record<CartTrackingEvent, comluxgroup.ClientEvent_1_0_0> = {
  FLIGHT_SELECT_PRESSED: {
    action: 'pressed',
    subject: 'flight_select',
    type: 'interaction',
    category: CART_CATEGORIES.InflowDepartingFlight,
  },
  DEPARTING_FLIGHT_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'departing_flight_page',
    type: 'nonInteraction',
    category: CART_CATEGORIES.InflowDepartingFlight,
  },
  VIEW_RETURN_FLIGHTS_PRESSED: {
    action: 'pressed',
    subject: 'view_return_flights',
    type: 'interaction',
    category: CART_CATEGORIES.InflowDepartingFlight,
  },
  RETURN_FLIGHT_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'return_flight_page',
    type: 'nonInteraction',
    category: CART_CATEGORIES.InflowDepartingFlight,
  },
  CONTINUE_TO_EXPERIENCE_UPSELL_PRESSED: {
    action: 'pressed',
    subject: 'continue',
    type: 'interaction',
    category: CART_CATEGORIES.InflowDepartingFlight,
  },
  EXPERIENCE_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'experiences_page',
    type: 'nonInteraction',
    category: CART_CATEGORIES.InflowExperiencesPage,
  },
  CONTINUE_TO_CHECKOUT_PRESSED: {
    action: 'pressed',
    subject: 'review_payment',
    type: 'interaction',
    category: CART_CATEGORIES.InflowExperiencesPage,
  },
  SKIP_EXPERIENCE_UPSELL: {
    action: 'pressed',
    subject: 'skip',
    type: 'interaction',
    category: CART_CATEGORIES.InflowExperiencesPage,
  },
  REVIEW_PAYMENT_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'review_payment_page',
    type: 'nonInteraction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  ADD_INSURANCE_PRESSED: {
    action: 'pressed',
    subject: 'add_insurance',
    type: 'interaction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  DONT_ADD_INSURANCE_PRESSED: {
    action: 'pressed',
    subject: 'dont_add_insurance',
    type: 'interaction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  PAYMENT_METHOD_PRESSED: {
    action: 'pressed',
    subject: 'payment_method',
    type: 'interaction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  PAY_PRESSED: {
    action: 'pressed',
    subject: 'pay',
    type: 'interaction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  CONFIRM_TRAVELLER_DETAILS_MODAL_VIEWED: {
    action: 'viewed',
    subject: 'confirm_traveller_details',
    type: 'nonInteraction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  CONFIRM_TRAVELLER_DETAILS_PRESSED: {
    action: 'pressed',
    subject: 'confirm_traveller_details',
    type: 'interaction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  TRANSACTION_STARTED: {
    action: 'started',
    subject: 'transaction',
    type: 'nonInteraction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  TRANSACTION_FAILED: {
    action: 'failed',
    subject: 'transaction',
    type: 'nonInteraction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  TRANSACTION_SUCCEEDED: {
    action: 'started',
    subject: 'transaction',
    type: 'nonInteraction',
    category: CART_CATEGORIES.ReviewPayment,
  },
  BOOKING_CONFIRMED_VIEWED: {
    action: 'viewed',
    subject: 'booking_confirmed',
    type: 'nonInteraction',
    category: CART_CATEGORIES.BookingConfirmed,
  },
}

function buildJourneyFlight(journeyFlight: App.JourneyFlight | null): comluxgroup.CartContext_1_0_0['flight']['journey']['departing'] | comluxgroup.CartContext_1_0_0['flight']['journey']['returning'] {
  if (!journeyFlight) {
    return null
  }
  const flightTime = {
    hours: journeyFlight.flightTime?.hours,
    minutes: journeyFlight.flightTime?.minutes,
  }
  const priceBreakdown = {
    adults: {
      cost: journeyFlight.priceBreakdown?.adults?.cost,
      tax: journeyFlight.priceBreakdown?.adults?.tax,
    },
    children: {
      cost: journeyFlight.priceBreakdown?.children?.cost,
      tax: journeyFlight.priceBreakdown?.children?.tax,
    },
    infants: {
      cost: journeyFlight.priceBreakdown?.infants?.cost,
      tax: journeyFlight.priceBreakdown?.infants?.tax,
    },
  }

  const flights = (journeyFlight.flights ?? []).map(flight => ({
    departingAirport: flight.departingAirport,
    departingDate: flight.departingDate,
    departingTime: flight.departingTime,
    arrivalAirport: flight.arrivalAirport,
    arrivalDate: flight.arrivalDate,
    arrivalTime: flight.arrivalTime,
    carrierName: flight.carrierName,
    fareClass: flight.fareClass,
    layoverTime: {
      hours: flight.layoverTime?.hours,
      minutes: flight.layoverTime?.minutes,
    },
  }))

  return {
    journeyKey: journeyFlight.journeyKey,
    flightTime,
    priceBreakdown,
    flights,
  }
}

export function buildCartContext(cartState: App.CartState, paymentType?: string, paymentErrorReason?: KnownPaymentErrorReasons): {
  schema: string;
  data: Record<string, unknown>;
} {
  const items = (cartState.items ?? []).map((item) => ({
    id: item.id,
    orderItemId: item.orderItemId,
    price: item.price,
    value: item.value,
    type: item.type,
    status: item.status,
  }))
  const flight: comluxgroup.CartContext_1_0_0['flight'] = {
    journey: {
      id: cartState.flight?.journey?.id,
      searchId: cartState.flight?.journey?.searchId,
      fareClass: cartState.flight?.journey?.fareClass,
      fareType: cartState.flight?.journey?.fareType,
      departing: buildJourneyFlight(cartState.flight?.journey?.departing),
      returning: buildJourneyFlight(cartState.flight?.journey?.returning),
    },
    passengers: (cartState.flight?.passengers ?? []).map(passenger => ({
      isPrimary: !!passenger.isPrimary,
      type: passenger.type,
    })),
  }
  const insurance = {
    transactionKey: cartState.insurance?.transactionKey,
    insuranceAccepted: cartState.insurance?.insuranceAccepted,
    insuranceProductId: cartState.insurance?.insuranceProductId,
  }
  return comluxgroup.createCartContext_1_0_0({
    paymentType,
    paymentErrorReason,
    transactionKey: cartState.transactionKey,
    items,
    currencyCode: cartState.currencyCode,
    flight,
    insurance,
  })
}

export type ExperienceEventDefinitionKey =
  | 'SAVE_TO_TRIP_BUTTON_CLICKED'
  | 'SHARE_BUTTON_CLICKED'
  | 'SHARE_OPTION_SELECTED'
  | 'TITLE_COPIED'
  | 'CHECK_AVAILABILITY_CLICKED'
  | 'GIFTING_AVAILABLE_CLICKED'
  | 'SUPPORT_TEAM_NUMBER_BUTTON_CLICKED'
  | 'SUPPORT_TEAM_EMAIL_BUTTON_CLICKED'
  | 'GIVE_AS_GIFT_CLICKED'
  | 'GIFT_COMPONENT_CLOSED'
  | 'GIFT_INCREASE_OPTION_SELECTED'
  | 'GIFT_DECREASE_OPTION_SELECTED'
  | 'GIFT_BUY_NOW_CLICKED'
  | 'INTEREST_FREE_INFO_CLICKED'
  | 'BNBL_BUTTON_CLICKED'
  | 'BNBL_LEARN_MORE_BUTTON_CLICKED'
  | 'BNBL_COMPONENT_CLOSED'
  | 'BNBL_INCREASE_OPTION_SELECTED'
  | 'BNBL_DECREASE_OPTION_SELECTED'
  | 'BNBL_BUY_NOW_CLICKED'
  | 'BROWSE_ALL_EXPERIENCES_CLICK'
  | 'CALENDAR_OPEN'
  | 'CALENDAR_CLOSE'
  | 'DATE_ON_CALENDAR_SELECTED'
  | 'DATE_ON_CALENDAR_SELECTED_NON_INTERACTION'
  | 'LANGUAGE_SELECTED'
  | 'NEXT_MONTH_CLICKED'
  | 'PREV_MONTH_CLICKED'
  | 'TIME_SLOT_SELECTED'
  | 'TIME_SLOT_SELECTED_NON_INTERACTION'
  | 'INCREASE_OPTION_SELECTED'
  | 'DECREASE_OPTION_SELECTED'
  | 'BUY_NOW_CLICKED'
  | 'CANCELLATION_POLICY_CLICKED'
  | 'INFO_HOW_TO_REDEEM_OPEN'
  | 'INFO_HOW_TO_REDEEM_CLOSE'
  | 'INFO_CANCELLATION_POLICY_OPEN'
  | 'INFO_CANCELLATION_POLICY_CLOSE'
  | 'INFO_AVAILABILITY_OPEN'
  | 'INFO_AVAILABILITY_CLOSE'
  | 'INFO_CONDITIONS_OF_OFFER_OPEN'
  | 'INFO_CONDITIONS_OF_OFFER_CLOSE'
  | 'INFO_VENDOR_INFORMATION_OPEN'
  | 'INFO_VENDOR_INFORMATION_CLOSE'
  | 'INFO_FINE_PRINT_INFORMATION_OPEN'
  | 'INFO_FINE_PRINT_INFORMATION_CLOSE'
  | 'PICKUP_LOCATION_SELECTED'
  | 'PICKUP_LOCATION_SELECTED_NON_INTERACTION'
  | 'REDEMPTION_LOCATION_SELECTED'
  | 'REDEMPTION_LOCATION_SELECTED_NON_INTERACTIVE'
  | 'GEOLOCATION_OPENED'
  | 'GEOLOCATION_CLOSED'
  | 'GEOLOCATION_USE_CURRENT_LOCATION_CLICKED'
  | 'GEOLOCATION_SELECTED'
  | 'CATEGORY_CLICKED'
  | 'OFFER_TILE_CLICKED'
  | 'OFFER_TILE_IMPRESSION'
  | 'CAROUSEL_NEXT_CLICKED'
  | 'CAROUSEL_PREV_CLICKED'
  | 'VIEW_ON_MAP_CLICKED'
  | 'VIEW_ALL_CLICKED'
  | 'TRENDING_TILE_CLICKED'
  | 'ARTICLE_TILE_CLICKED'
  | 'CHECKOUT_SKIP_CLICKED'
  | 'CHECKOUT_CONTINUE_CLICKED'

export const experienceOfferEventsMap: Record<ExperienceEventDefinitionKey, Omit<comluxgroup.ClientEvent_1_0_0, 'category'>> = {
  SAVE_TO_TRIP_BUTTON_CLICKED: {
    action: 'pressed',
    subject: 'save to trip',
    type: 'interaction',
  },
  SHARE_BUTTON_CLICKED: {
    action: 'pressed',
    subject: 'share button',
    type: 'interaction',
  },
  SHARE_OPTION_SELECTED: {
    action: 'selected',
    subject: 'share option',
    type: 'interaction',
  },
  TITLE_COPIED: {
    action: 'copied',
    subject: 'experience title',
    type: 'interaction',
  },
  CHECK_AVAILABILITY_CLICKED: {
    action: 'pressed',
    subject: 'check availability',
    type: 'interaction',
  },
  GIFTING_AVAILABLE_CLICKED: {
    action: 'pressed',
    subject: 'gifting available',
    type: 'interaction',
  },
  SUPPORT_TEAM_NUMBER_BUTTON_CLICKED: {
    action: 'pressed',
    subject: 'support team number button',
    type: 'interaction',
  },
  SUPPORT_TEAM_EMAIL_BUTTON_CLICKED: {
    action: 'pressed',
    subject: 'support team email button',
    type: 'interaction',
  },
  GIVE_AS_GIFT_CLICKED: {
    action: 'pressed',
    subject: 'give as gift',
    type: 'interaction',
  },
  GIFT_COMPONENT_CLOSED: {
    action: 'closed',
    subject: 'gift component',
    type: 'interaction',
  },
  GIFT_INCREASE_OPTION_SELECTED: {
    action: 'selected',
    subject: 'gift increase option',
    type: 'interaction',
  },
  GIFT_DECREASE_OPTION_SELECTED: {
    action: 'selected',
    subject: 'gift decrease option',
    type: 'interaction',
  },
  GIFT_BUY_NOW_CLICKED: {
    action: 'pressed',
    subject: 'gift buy now',
    type: 'interaction',
  },
  BNBL_LEARN_MORE_BUTTON_CLICKED: {
    action: 'pressed',
    subject: 'bnbl lean more button',
    type: 'interaction',
  },
  INTEREST_FREE_INFO_CLICKED: {
    action: 'pressed',
    subject: 'interest free info button',
    type: 'interaction',
  },
  BNBL_BUTTON_CLICKED: {
    action: 'pressed',
    subject: 'bnbl buy now',
    type: 'interaction',
  },
  BNBL_COMPONENT_CLOSED: {
    action: 'closed',
    subject: 'bnbl component',
    type: 'interaction',
  },
  BNBL_INCREASE_OPTION_SELECTED: {
    action: 'selected',
    subject: 'bnbl increase option',
    type: 'interaction',
  },
  BNBL_DECREASE_OPTION_SELECTED: {
    action: 'selected',
    subject: 'bnbl decrease option',
    type: 'interaction',
  },
  BNBL_BUY_NOW_CLICKED: {
    action: 'pressed',
    subject: 'bnbl buy now',
    type: 'interaction',
  },
  BROWSE_ALL_EXPERIENCES_CLICK: {
    action: 'pressed',
    subject: 'browse all experiences',
    type: 'interaction',
  },
  CALENDAR_OPEN: {
    action: 'opened',
    subject: 'calendar',
    type: 'interaction',
  },
  CALENDAR_CLOSE: {
    action: 'closed',
    subject: 'calendar',
    type: 'interaction',
  },
  DATE_ON_CALENDAR_SELECTED: {
    action: 'selected',
    subject: 'date on calendar',
    type: 'interaction',
  },
  DATE_ON_CALENDAR_SELECTED_NON_INTERACTION: {
    action: 'selected',
    subject: 'date on calendar',
    type: 'nonInteraction',
  },
  NEXT_MONTH_CLICKED: {
    action: 'pressed',
    subject: 'next month',
    type: 'interaction',
  },
  PREV_MONTH_CLICKED: {
    action: 'pressed',
    subject: 'prev month',
    type: 'interaction',
  },
  TIME_SLOT_SELECTED: {
    action: 'selected',
    subject: 'time slot',
    type: 'interaction',
  },
  TIME_SLOT_SELECTED_NON_INTERACTION: {
    action: 'selected',
    subject: 'time slot',
    type: 'nonInteraction',
  },
  INCREASE_OPTION_SELECTED: {
    action: 'selected',
    subject: 'increase option',
    type: 'interaction',
  },
  DECREASE_OPTION_SELECTED: {
    action: 'selected',
    subject: 'decrease option',
    type: 'interaction',
  },
  BUY_NOW_CLICKED: {
    action: 'pressed',
    subject: 'buy now',
    type: 'interaction',
  },
  CANCELLATION_POLICY_CLICKED: {
    action: 'pressed',
    subject: 'cancellation policy',
    type: 'interaction',
  },
  INFO_HOW_TO_REDEEM_OPEN: {
    action: 'opened',
    subject: 'info how to redeem',
    type: 'interaction',
  },
  INFO_CANCELLATION_POLICY_OPEN: {
    action: 'opened',
    subject: 'info cancellation policy',
    type: 'interaction',
  },
  INFO_AVAILABILITY_OPEN: {
    action: 'opened',
    subject: 'info availability',
    type: 'interaction',
  },
  INFO_CONDITIONS_OF_OFFER_OPEN: {
    action: 'opened',
    subject: 'info conditions of Experience',
    type: 'interaction',
  },
  INFO_VENDOR_INFORMATION_OPEN: {
    action: 'opened',
    subject: 'info vendor information',
    type: 'interaction',
  },
  INFO_FINE_PRINT_INFORMATION_OPEN: {
    action: 'opened',
    subject: 'info fine print information',
    type: 'interaction',
  },
  INFO_HOW_TO_REDEEM_CLOSE: {
    action: 'closed',
    subject: 'info how to redeem',
    type: 'interaction',
  },
  INFO_CANCELLATION_POLICY_CLOSE: {
    action: 'closed',
    subject: 'info cancellation policy',
    type: 'interaction',
  },
  INFO_AVAILABILITY_CLOSE: {
    action: 'closed',
    subject: 'info availability',
    type: 'interaction',
  },
  INFO_CONDITIONS_OF_OFFER_CLOSE: {
    action: 'closed',
    subject: 'info conditions of Experience',
    type: 'interaction',
  },
  INFO_VENDOR_INFORMATION_CLOSE: {
    action: 'closed',
    subject: 'info vendor information',
    type: 'interaction',
  },
  INFO_FINE_PRINT_INFORMATION_CLOSE: {
    action: 'closed',
    subject: 'info fine print information',
    type: 'interaction',
  },
  LANGUAGE_SELECTED: {
    action: 'selected',
    subject: 'language',
    type: 'interaction',
  },
  PICKUP_LOCATION_SELECTED: {
    action: 'selected',
    subject: 'pickup location',
    type: 'interaction',
  },
  PICKUP_LOCATION_SELECTED_NON_INTERACTION: {
    action: 'selected',
    subject: 'pickup location',
    type: 'nonInteraction',
  },
  REDEMPTION_LOCATION_SELECTED: {
    action: 'selected',
    subject: 'redemption location',
    type: 'interaction',
  },
  REDEMPTION_LOCATION_SELECTED_NON_INTERACTIVE: {
    action: 'selected',
    subject: 'redemption location',
    type: 'nonInteraction',
  },
  GEOLOCATION_OPENED: {
    action: 'opened',
    subject: 'geo location',
    type: 'interaction',
  },
  GEOLOCATION_CLOSED: {
    action: 'closed',
    subject: 'geo location',
    type: 'interaction',
  },
  GEOLOCATION_USE_CURRENT_LOCATION_CLICKED: {
    action: 'pressed',
    subject: 'use my current location',
    type: 'interaction',
  },
  GEOLOCATION_SELECTED: {
    action: 'pressed',
    subject: 'location',
    type: 'interaction',
  },
  CATEGORY_CLICKED: {
    action: 'pressed',
    subject: 'experiences category tile',
    type: 'interaction',
  },
  OFFER_TILE_CLICKED: {
    action: 'pressed',
    subject: 'offer tile',
    type: 'interaction',
  },
  OFFER_TILE_IMPRESSION: {
    action: 'impression',
    subject: 'offer tile',
    type: 'nonInteraction',
  },
  CAROUSEL_NEXT_CLICKED: {
    action: 'pressed',
    subject: 'carousel next button',
    type: 'interaction',
  },
  CAROUSEL_PREV_CLICKED: {
    action: 'pressed',
    subject: 'carousel prev button',
    type: 'interaction',
  },
  VIEW_ON_MAP_CLICKED: {
    action: 'pressed',
    subject: 'view on map',
    type: 'interaction',
  },
  VIEW_ALL_CLICKED: {
    action: 'pressed',
    subject: 'view all',
    type: 'interaction',
  },
  TRENDING_TILE_CLICKED: {
    action: 'pressed',
    subject: 'trending destination tile',
    type: 'interaction',
  },
  ARTICLE_TILE_CLICKED: {
    action: 'pressed',
    subject: 'article tile',
    type: 'interaction',
  },
  CHECKOUT_SKIP_CLICKED: {
    action: 'pressed',
    subject: 'checkout skip button',
    type: 'interaction',
  },
  CHECKOUT_CONTINUE_CLICKED: {
    action: 'pressed',
    subject: 'checkout continue button',
    type: 'interaction',
  },
}

interface ExperienceItemProps {
  packageId?: string;
  typeId?: string;
  rateId?: string;
  variant?: string;
  travelStart?: string;
  travelEnd?: string;
  month?: string;
  year?: string;
  duration?: number;
  numberOfAdults?: number;
  numberOfChildren?: number;
  numberOfInfants?: number;
  childrenAges?: number[];
  value?: number;
  price?: number;
  surcharge?: number;
  propertyFees?: number;
  currency: string;
  region: string;
  brand: string;
  reservationType?: string;
  bookingNumber?: string;
  status?: string;
  orderId?: string;
  itemId?: string;
  metadata?: string;
}

interface ExperienceListProps {
  listId?: string;
  listName?: string;
  listSource?: string;
}

interface ExperienceOfferEventContextData {
  offer: App.ExperienceOffer;
  itemProps?: ExperienceItemProps;
  listProps?: ExperienceListProps;
  geo: App.GeoState;
}

export interface ExperienceOfferEventContext {
  type: 'impression' | 'click' | 'item';
  data: ExperienceOfferEventContextData;
}

function itemToEventContext(event: ExperienceOfferEventContextData) {
  return comluxgroup.createItem_1_0_0({
    ...event.itemProps,
    brand: config.BRAND,
    offerId: event.offer.id,
    offerType: event.offer.productType,
    currency: event.geo.currentCurrency,
    region: event.geo.currentRegionCode,
  })
}

function getProductAction(event: ExperienceOfferEventContextData) {
  return {
    ...event.listProps,
    id: event.offer.id,
    name: event.offer.name,
    currency: event.geo.currentCurrency,
    productType: event.offer.productType,
    price: event.offer.price,
  }
}

function productClickEventContext(event: ExperienceOfferEventContextData) {
  return comluxgroup.createProductClickContext_1_0_1(getProductAction(event))
}

function productImpressionContext(event: ExperienceOfferEventContextData) {
  return comluxgroup.createProductImpressionContext_1_0_1(getProductAction(event))
}

export function getExperienceContext(eventContexts: Array<ExperienceOfferEventContext>): Array<SelfDescribingJson> {
  return eventContexts.map((eventContext) => {
    if (eventContext.type === 'impression') {
      return productImpressionContext(eventContext.data)
    }
    if (eventContext.type === 'click') {
      return productClickEventContext(eventContext.data)
    }
    // then eventContext.type === 'item'
    return itemToEventContext(eventContext.data)
  })
}

export interface ExperienceEventEntity {
  data: {
    offer: App.ExperienceOffer;
    itemProps?: ExperienceItemProps;
    listProps?: ExperienceListProps;
  }
  type: 'impression' | 'click' | 'item'
}

const SEARCH_CATEGORY = 'searchFilters'

function buildOfferContext(offerId: string, offerType: string, offerPageContext: OfferPageState): comluxgroup.OfferContext_2_1_0 {
  const airport = {
    code: offerPageContext.airport?.code,
    name: offerPageContext.airport?.name,
    latitude: offerPageContext.airport?.latitude,
    longitude: offerPageContext.airport?.longitude,
    requiresAPIS: offerPageContext.airport?.requiresAPIS,
  }
  const multipleRoomPackages = offerPageContext.multipleRoomPackages?.map((room) => ({
    roomId: room.roomId,
    packageUniqueKey: room.packageUniqueKey,
    isFlightsSelected: room.isFlightsSelected,
    roomTypeId: room.roomTypeId,
    childrenAge: [],
  })) ?? []

  const rooms = offerPageContext.rooms?.map((room) => ({
    roomId: room.roomId,
    adults: room.adults,
    children: room.children,
    infants: room.infants,
    childrenAge: room.childrenAge,
  })) ?? []

  const ctx: comluxgroup.OfferContext_2_1_0 = {
    offerId,
    offerType,
    state: {
      airport,
      checkInDate: offerPageContext.checkInDate?.format(ISO_DATE_FORMAT),
      checkOutDate: offerPageContext.checkOutDate?.format(ISO_DATE_FORMAT),
      duration: offerPageContext.duration,
      multipleRoomPackages,
      rooms,
      room: {
        id: offerPageContext.room?.id ?? null,
      },
      activeFilter: offerPageContext.activeFilter,
      activeSubFilter: offerPageContext.activeSubFilter,
      flights: offerPageContext.flights,
      isMultiRoomConnected: offerPageContext.isMultiRoomConnected,
      occupancyPicker: offerPageContext.occupancyPicker,
      package: {
        idSalesforceExternal: offerPageContext.package?.id ?? null,
        uniqueKey: offerPageContext.package?.uniqueKey ?? null,
        lePackageId: offerPageContext.package?.id ?? null,
      },
      recommendationsCheckInDay: offerPageContext.recommendationsCheckInDay,
      recommendationsMonthIndex: offerPageContext.recommendationsMonthIndex,
      showRecommendationsView: offerPageContext.showRecommendationsView,
      roomOption: {
        roomRateId: offerPageContext.roomOption?.roomRateId ?? null,
        roomTypeId: offerPageContext.roomOption?.roomTypeId ?? null,
      },
      selectedDepartureMonthIndex: offerPageContext.selectedDepartureMonthIndex,
      selectedDepartureYear: offerPageContext.selectedDepartureYear,
      selectedPackageView: {
        package: {
          idSalesforceExternal: offerPageContext.selectedPackageView?.package?.id ?? null,
          uniqueKey: offerPageContext.selectedPackageView?.package?.uniqueKey ?? null,
          lePackageId: offerPageContext.selectedPackageView?.package?.id ?? null,
        },
      },
      // hasTactical: offerPageContext.offer?.hasTactical // reinstate once updated schema & context is used
    },
  }
  return ctx
}

export const offerEventsMap: Record<OfferContextEvent, Omit<comluxgroup.ClientEvent_1_0_0, 'category'>> = {
  UPGRADE_MODAL_OPENED: {
    action: 'opened',
    subject: 'upgrade_modal',
    type: 'interaction',
  },
  UPGRADE_MODAL_ACCEPTED: {
    action: 'accepted',
    subject: 'upgrade_modal',
    type: 'interaction',
  },
  UPGRADE_MODAL_REJECTED: {
    action: 'rejected',
    subject: 'upgrade_modal',
    type: 'interaction',
  },
  UPGRADE_MODAL_CLOSED: {
    action: 'closed',
    subject: 'upgrade_modal',
    type: 'interaction',
  },
  BOOK_NOW_PRESSED: {
    action: 'pressed',
    subject: 'book_now',
    type: 'interaction',
  },
  BUY_NOW_CHOOSE_DATES_LATER_PRESSED: {
    action: 'pressed',
    subject: 'buy_now_choose_dates_later',
    type: 'interaction',
  },
  CHECK_AVAILABILITY_PRESSED: {
    action: 'pressed',
    subject: 'check_availability',
    type: 'interaction',
  },
  VIEW_PACKAGE_OPTIONS_PRESSED: {
    action: 'pressed',
    subject: 'view_package_options',
    type: 'interaction',
  },
  DURATION_PRESSED: {
    action: 'pressed',
    subject: 'duration',
    type: 'interaction',
  },
  OCCUPANCY_FILTERS_APPLIED: {
    action: 'applied',
    subject: 'guests',
    type: 'interaction',
  },
  OCCUPANCY_FILTERS_PRESSED: {
    action: 'pressed',
    subject: 'guests',
    type: 'interaction',
  },
  OCCUPANCY_FILTERS_CLOSED: {
    action: 'closed',
    subject: 'guests',
    type: 'interaction',
  },
  CAPACITY_HOTEL_FILTER_OPENED: {
    action: 'opened',
    subject: 'guests',
    type: 'interaction',
  },
  CALENDAR_MONTH_SELECTED: {
    action: 'selected',
    subject: 'calendar_month',
    type: 'interaction',
  },
  CALENDAR_CHECK_IN_SELECTED: {
    action: 'selected',
    subject: 'calendar_check_in',
    type: 'interaction',
  },
  CALENDAR_FILTER_OPENED: {
    action: 'opened',
    subject: 'when',
    type: 'nonInteraction',
  },
  CALENDAR_TOGGLE_PRESSED: {
    action: 'pressed',
    subject: 'when',
    type: 'interaction',
  },
  CALENDAR_CLOSED: {
    action: 'closed',
    subject: 'when',
    type: 'interaction',
  },
  CALENDAR_DATE_CHANGED: {
    action: 'changed',
    subject: 'when',
    type: 'interaction',
  },
  CALENDAR_FILTERS_APPLY: {
    action: 'applied',
    subject: 'when',
    type: 'interaction',
  },
  CALENDAR_HOTEL_TOGGLED: {
    action: 'toggled',
    subject: 'when_hotel_flight',
    type: 'interaction',
  },
  CALENDAR_SUB_FILTER_OPENED: {
    action: 'opened',
    subject: 'when_subfilter',
    type: 'interaction',
  },
  CALENDAR_SUB_FILTER_CLOSED: {
    action: 'closed',
    subject: 'when_subfilter',
    type: 'interaction',
  },
  AIRPORT_FILTER_CLOSED: {
    action: 'closed',
    subject: 'airport',
    type: 'interaction',
  },
  AIRPORT_FILTER_PRESSED: {
    action: 'pressed',
    subject: 'airport',
    type: 'interaction',
  },
  AIRPORT_FILTER_SELECTED: {
    action: 'selected',
    subject: 'airport',
    type: 'interaction',
  },
  HOTEL_NAME_SELECTED: {
    action: 'selected',
    subject: 'hotel_name',
    type: 'interaction',
  },
  DURATION_FILTER_SELECTED: {
    action: 'selected',
    subject: 'duration',
    type: 'interaction',
  },
  DURATION_FILTER_CLOSED: {
    action: 'closed',
    subject: 'duration',
    type: 'interaction',
  },
  DURATION_FILTER_PRESSED: {
    action: 'pressed',
    subject: 'duration',
    type: 'interaction',
  },
  VIEW_TOUR_OPTIONS_PRESSED: {
    action: 'pressed',
    subject: 'tour_option',
    type: 'interaction',
  },
  VIEW_TOUR_OPTIONS_EXTENDED_PRESSED: {
    action: 'pressed',
    subject: 'tour_option_ext',
    type: 'interaction',
  },
  VIEW_TOUR_OPTIONS_EXTENDED_ALT_PRESSED: {
    action: 'pressed',
    subject: 'tour_option_ext_alt',
    type: 'interaction',
  },
}

export function getOfferContext(offerPageContext: OfferPageState | undefined): {
  schema: string;
  data: Record<string, unknown>;
} | null {
  if (offerPageContext?.offer) {
    const offer = offerPageContext.offer
    const ctx = buildOfferContext(offer.id, offer.type, offerPageContext)
    return comluxgroup.createOfferContext_2_1_0(ctx)
  }
  if (offerPageContext?.bedbankOffer) {
    const bedbankOffer = offerPageContext.bedbankOffer
    const ctx = buildOfferContext(bedbankOffer.id, bedbankOffer.type, offerPageContext)
    return comluxgroup.createOfferContext_2_1_0(ctx)
  }
  if (offerPageContext?.tourV2Offer) {
    const tourV2Offer = offerPageContext.tourV2Offer
    const ctx = buildOfferContext(tourV2Offer.id, tourV2Offer.type, offerPageContext)
    return comluxgroup.createOfferContext_2_1_0(ctx)
  }
  return null
}

export enum FrontPageEvent {
  FILTERS_APPLIED = 'FILTERS_APPLIED',
  FILTERS_RESET = 'FILTERS_RESET',
  MAP_VIEW_OPENED = 'MAP_VIEW_OPENED',
  LIST_VIEW_OPENED = 'LIST_VIEW_OPENED',
}

export const frontPageEventsMap: Record<FrontPageEvent, comluxgroup.ClientEvent_1_0_0> = {
  FILTERS_APPLIED: {
    action: 'applied',
    subject: 'offerListFilters',
    type: 'interaction',
    category: SEARCH_CATEGORY,
  },
  FILTERS_RESET: {
    action: 'reset',
    subject: 'offerListFilters',
    type: 'interaction',
    category: SEARCH_CATEGORY,
  },
  MAP_VIEW_OPENED: {
    action: 'clicked',
    subject: 'mapViewButton',
    type: 'interaction',
    category: SEARCH_CATEGORY,
  },
  LIST_VIEW_OPENED: {
    action: 'clicked',
    subject: 'listViewButton',
    type: 'interaction',
    category: SEARCH_CATEGORY,
  },
}

type SeachContextOverrideParams = Omit<Partial<comluxgroup.Search_1_0_0>, 'searchParams'> & { searchParams: Partial<comluxgroup.Search_1_0_0['searchParams']>}

export function buildSearchContext(override?: SeachContextOverrideParams) {
  return {
    verticals: [],
    searchBar: '',
    searchType: '',
    searchSessionId: '',
    searchResults: [],
    ...override,
    searchParams: {
      landmarkId: null,
      propertyId: null,
      destinationId: null,
      checkIn: null,
      checkOut: null,
      isFlexibleDates: false,
      occupancies: [],
      sort: {
        isPristine: true,
      },
      filters: {
        holidayTypes: [],
        locations: [],
        amenities: [],
      },
      ...override?.searchParams,
    },
  }
}

export function mapToSnowplowFilters(existingFilters: App.OfferListFilters): comluxgroup.Search_1_0_0['searchParams']['filters'] {
  return {
    holidayTypes: existingFilters.holidayTypes ?? [],
    locations: existingFilters.locations ?? [],
    amenities: existingFilters.amenities ?? [],
    propertyTypes: existingFilters.propertyTypes ?? [],
    categories: existingFilters.categories ?? [],
  }
}
export interface SaveToTripBookingDetails {
  rooms?: Array<App.Occupants>;
  startDate?: string;
  endDate?: string;
  packageId?: string;
}

export function createOfferItemContexts(
  offer: App.AnyOffer,
  regionCode: string,
  currency: string,
  bookingDetails: SaveToTripBookingDetails = {},
): Array<ReturnType<typeof comluxgroup.createItem_1_0_0>> {
  const hasDates = !!(bookingDetails.startDate && bookingDetails.endDate)
  const duration = hasDates ? moment(bookingDetails?.endDate).diff(bookingDetails?.startDate, 'days') : undefined
  const itemContexts: Array<ReturnType<typeof comluxgroup.createItem_1_0_0>> = []

  if (bookingDetails.rooms) {
    bookingDetails.rooms.forEach(room => {
      itemContexts.push(comluxgroup.createItem_1_0_0({
        offerId: offer.id,
        offerType: offer.type,
        region: regionCode,
        packageId: bookingDetails.packageId,
        currency,
        travelStart: bookingDetails?.startDate,
        travelEnd: bookingDetails?.endDate,
        duration,
        childrenAges: room.childrenAge,
        numberOfAdults: room.adults,
        numberOfChildren: room.children,
        numberOfInfants: room.infants,
        brand: config.BRAND,
      }))
    })
  } else {
    itemContexts.push(comluxgroup.createItem_1_0_0({
      offerId: offer.id,
      offerType: offer.type,
      packageId: bookingDetails.packageId,
      travelStart: bookingDetails?.startDate,
      travelEnd: bookingDetails?.endDate,
      duration,
      region: regionCode,
      currency,
      brand: config.BRAND,
    }))
  }

  return itemContexts
}

export function createTourOfferItemContexts(
  offer: Tours.V2Offer,
  regionCode: string,
  currency: string,
  tourItems: Array<App.Checkout.TourV2Item> = [],
): Array<ReturnType<typeof comluxgroup.createItem_1_0_0>> {
  const itemContexts: Array<ReturnType<typeof comluxgroup.createItem_1_0_0>> = []

  if (tourItems.length > 0) {
    tourItems.forEach((tourItem: App.Checkout.TourV2Item) => {
      const hasDates = !!(tourItem.startDate && tourItem.endDate)
      const duration = hasDates ? moment(tourItem?.endDate).diff(tourItem?.startDate, 'days') : undefined
      itemContexts.push(comluxgroup.createItem_1_0_0({
        offerId: offer.id,
        offerType: offer.type,
        region: regionCode,
        packageId: tourItem?.purchasableOption?.fkVariationId,
        currency,
        travelStart: tourItem?.startDate,
        travelEnd: tourItem?.endDate,
        duration,
        childrenAges: tourItem?.occupancy?.childrenAge,
        numberOfAdults: tourItem?.occupancy?.adults,
        numberOfChildren: tourItem?.occupancy?.children,
        numberOfInfants: tourItem?.occupancy?.infants,
        brand: config.BRAND,
      }))
    })
  }

  return itemContexts
}

export function toExperimentKey(experimentId: string) {
  return `exp_${experimentId}`
}

/**
 * Constants for cookie setting
 */
const cookieDisabledIndex = 0
const domainUserIdIndex = 1
const createTsIndex = 2
const visitCountIndex = 3
const nowTsIndex = 4
const lastVisitTsIndex = 5
const sessionIdIndex = 6
const previousSessionIdIndex = 7
const firstEventIdIndex = 8
const firstEventTsInMsIndex = 9
const eventIndexIndex = 10

const configVisitorCookieTimeout = 63072000 // 2 years
const configCookiePath = '/'
const configCookieDomain = undefined
const configCookieSameSite = 'None'

/**
 * Parses the cookie values from its string representation.
 *
 * @param id Cookie value as string
 * @param domainUserId Domain user ID to be used in case of empty cookie string
 * @returns Parsed ID cookie tuple
 */
function parseIdCookie(id: string, domainUserId?: string, memorizedSessionId?: string, memorizedVisitCount?: number) {
  const now = new Date(); const nowTs = Math.round(now.getTime() / 1000); let tmpContainer
  if (id) {
    tmpContainer = id.split('.')
    // cookies enabled
    tmpContainer.unshift('0')
  }
  else {
    tmpContainer = [
      // cookies disabled
      '1',
      // Domain user ID
      domainUserId,
      // Creation timestamp - seconds since Unix epoch
      nowTs,
      // visitCount - 0 = no previous visit
      memorizedVisitCount,
      // Current visit timestamp
      nowTs,
      // Last visit timestamp - blank meaning no previous visit
      '',
      // Session ID
      memorizedSessionId,
    ]
  }
  if (!tmpContainer[sessionIdIndex] || tmpContainer[sessionIdIndex] === 'undefined') {
    // session id
    tmpContainer[sessionIdIndex] = uuidV4()
  }
  if (!tmpContainer[previousSessionIdIndex] || tmpContainer[previousSessionIdIndex] === 'undefined') {
    // previous session id
    tmpContainer[previousSessionIdIndex] = ''
  }
  if (!tmpContainer[firstEventIdIndex] || tmpContainer[firstEventIdIndex] === 'undefined') {
    // firstEventId - blank meaning no previous event
    tmpContainer[firstEventIdIndex] = ''
  }
  if (!tmpContainer[firstEventTsInMsIndex] || tmpContainer[firstEventTsInMsIndex] === 'undefined') {
    // firstEventTs - blank meaning no previous event
    tmpContainer[firstEventTsInMsIndex] = ''
  }
  if (!tmpContainer[eventIndexIndex] || tmpContainer[eventIndexIndex] === 'undefined') {
    // eventIndex – 0 = no previous event
    tmpContainer[eventIndexIndex] = 0
  }
  const parseIntOr = function(value, defaultValue) {
    const parsed = parseInt(value)
    return isNaN(parsed) ? defaultValue : parsed
  }
  const parseIntOrUndefined = function(value) { return (value ? parseIntOr(value, undefined) : undefined) }
  const parsed = [
    tmpContainer[cookieDisabledIndex],
    tmpContainer[domainUserIdIndex],
    parseIntOr(tmpContainer[createTsIndex], nowTs),
    parseIntOr(tmpContainer[visitCountIndex], memorizedVisitCount),
    parseIntOr(tmpContainer[nowTsIndex], nowTs),
    parseIntOrUndefined(tmpContainer[lastVisitTsIndex]),
    tmpContainer[sessionIdIndex],
    tmpContainer[previousSessionIdIndex],
    tmpContainer[firstEventIdIndex],
    parseIntOrUndefined(tmpContainer[firstEventTsInMsIndex]),
    parseIntOr(tmpContainer[eventIndexIndex], 0),
  ]
  return parsed
}

export function overrideTrackerIDs(tracker: BrowserTracker, sessionId?: string, domainUserId?: string, sessionIdx?: number) {
  const idCookieName = tracker.getCookieName('id') as any as string
  const sessionIdCookieName = tracker.getCookieName('ses') as any as string
  const cookiesDict = getCookies()

  if (!cookiesDict[sessionIdCookieName]) {
    return
  }
  const parsed = parseIdCookie(cookiesDict[idCookieName])

  if (sessionId !== undefined) {
    parsed[sessionIdIndex] = sessionId
  }

  if (domainUserId === 'null') {
    console.warn('overrideTrackerIDs is getting stringified null as domainUserId')
  } else if (domainUserId !== undefined) {
    parsed[domainUserIdIndex] = domainUserId
  }

  if (sessionIdx !== undefined) {
    parsed[visitCountIndex] = sessionIdx
  }

  parsed.shift() // Remove "0" or "1"

  setCookie(
    idCookieName,
    parsed.join('.'),
    configVisitorCookieTimeout,
    configCookiePath,
    configCookieSameSite,
    configCookieDomain,
  )
}

function getHotelOrTourOffer(
  state: App.State,
  offerId: string,
  variationId?: string,
): App.Offer | App.BedbankOffer | App.OfferSummary | undefined {
  const offers = state.offer.offers
  const bedBankOffers = state.offer.bedbankOffers
  const offerSummary = state.offer.offerSummaries

  if (variationId && offerSummary[[offerId, variationId].join('/')]) {
    return offerSummary[[offerId, variationId].join('/')]
  } else if (offerSummary[offerId]) {
    return offerSummary[offerId]
  } else if (variationId && offers[[offerId, variationId].join('/')]) {
    return offers[[offerId, variationId].join('/')] || bedBankOffers[[offerId, variationId].join('/')]
  }
  return offers[offerId] || bedBankOffers[offerId]
}

function getCruiseTourV2OrExperienceOffer(
  state: App.State,
  offerId: string,
  variationId?: string,
): Tours.V2Offer | App.CruiseOffer | App.ExperienceOffer | undefined {
  const tourV2Offers = state.offer.tourV2Offers
  const cruiseOffers = state.cruise.cruiseOffers
  const experienceOffers = state.experience.experiences

  if (variationId) {
    const offerIdWithVariation = [offerId, variationId].join('/')
    if (tourV2Offers[offerIdWithVariation]) return tourV2Offers[offerIdWithVariation]
    if (cruiseOffers[offerIdWithVariation]) return cruiseOffers[offerIdWithVariation]
    if (experienceOffers[offerIdWithVariation]) return experienceOffers[offerIdWithVariation]
  }

  if (tourV2Offers[offerId]) return tourV2Offers[offerId]
  if (cruiseOffers[offerId]) return cruiseOffers[offerId]
  if (experienceOffers[offerId]) return experienceOffers[offerId]
}

export function getItemsContextForOffer(state: App.State) {
  const offer = getOffer(state)
  if (!offer) return

  const { hotelOrTourOffer, otherOffers, variationId } = offer
  if (!hotelOrTourOffer && !otherOffers) return

  if (otherOffers) {
    const { categoryId, productId } = getCategoryAndProductId(otherOffers.parentType, otherOffers)

    let offerLeadPrice: number | null = null
    let offerLeadUnit: string | null = null
    let offerLeadDuration: string | null = null
    let destinationCity: string | null = null
    let destinationCountry: string | null = null
    let originCity: string | null = null
    let originCountry: string | null = null

    if (otherOffers.parentType === 'cruise') {
      offerLeadDuration = String(otherOffers.duration)
      offerLeadPrice = otherOffers.lowestDeparturePrice.total

      const destinationCityAndCountry = otherOffers.returnPort.split(', ')
      const originCityAndCountry = otherOffers.departurePort.split(', ')
      if (destinationCityAndCountry.length > 1) {
        destinationCity = destinationCityAndCountry[0]
        destinationCountry = destinationCityAndCountry[1]
      }
      if (originCityAndCountry.length > 1) {
        originCity = originCityAndCountry[0]
        originCountry = originCityAndCountry[1]
      }
    }

    if (otherOffers.parentType === 'tour_v2') {
      const purchasableOptions = otherOffers.purchasableOptions
      if (purchasableOptions.length > 0) {
        const offerLead = purchasableOptions[0]

        const departure = otherOffers.departures[offerLead.fkDepartureId]
        const startDate = departure.startDate
        const endDate = departure.endDate

        // Sum 1 on days result because tours include the last day as duration too
        offerLeadDuration = (dateDifference(new Date(endDate), new Date(startDate)).days + 1).toString()
        offerLeadPrice = offerLead.price
      }

      offerLeadUnit = otherOffers.saleUnit

      if (variationId) {
        const offerVariations = otherOffers.variations[variationId]

        destinationCity = offerVariations.endLocation
        destinationCountry = offerVariations.countriesVisited.length > 0 ? offerVariations.countriesVisited[0] : null
        originCity = offerVariations.startLocation
        originCountry = offerVariations.countriesVisited.length > 0 ? offerVariations.countriesVisited.at(offerVariations.countriesVisited.length - 1) ?? null : null
      }
    }

    if (otherOffers.parentType === 'experience') {
      // In some scenarios it could be flexible but analytics teams said to keep duration 1
      offerLeadDuration = '1'
      offerLeadPrice = otherOffers.price
      offerLeadUnit = otherOffers.ticketUnitLabel ?? null

      const locations = getExperienceLocations(otherOffers.location.name)
      destinationCity = locations.destinationCity
      originCity = locations.originCity
      destinationCountry = locations.destinationCountry
      originCountry = locations.originCountry
    }

    return filterDefinedProperties([comluxgroup.createItem_1_1_0({
      offerId: otherOffers.id,
      categoryId,
      productId,
      offerLeadPrice,
      offerLeadUnit,
      offerLeadDuration,
      currency: state.geo.currentCurrency,
      destinationCity,
      destinationCountry,
      originCity,
      originCountry,
    })])
  } else if (hotelOrTourOffer) {
    const offerId = hotelOrTourOffer.id

    const { categoryId, productId } = getCategoryAndProductId(hotelOrTourOffer.parentType, hotelOrTourOffer)

    let offerLead: App.Package | undefined
    let offerGeoData: App.OfferPropertyGeoData | undefined

    if (hotelOrTourOffer.type !== 'bedbank_hotel') {
      offerLead = hotelOrTourOffer.lowestPricePackage
      offerGeoData = hotelOrTourOffer.property?.geoData
    }

    return filterDefinedProperties([comluxgroup.createItem_1_1_0({
      offerId,
      categoryId,
      productId,
      offerLeadPrice: offerLead?.price,
      offerLeadDuration: offerLead?.duration ? String(offerLead?.duration) : undefined,
      offerLeadUnit: hotelOrTourOffer.saleUnit,
      currency: state.geo.currentCurrency,
      destinationCity: offerGeoData?.locality,
      destinationRegion: offerGeoData?.administrativeAreaLevel1,
      destinationCountry: offerGeoData?.country,
    })])
  }
}

export function getItemsContextForCheckout(state: App.State) {
  const checkoutItems = state.checkout.cart.items

  const items = checkoutItems.reduce((items, currentItem) => {
    if (currentItem.itemType === 'experience') {
      const offerId = currentItem.experienceId

      const offer = getCruiseTourV2OrExperienceOffer(state, offerId) as App.ExperienceOffer

      const { categoryId, productId } = getCategoryAndProductId(currentItem.itemType, offer)

      const locations = getExperienceLocations(offer.location.name)
      const destinationCity = locations.destinationCity
      const originCity = locations.originCity
      const destinationCountry = locations.destinationCountry
      const originCountry = locations.originCountry

      const experienceItem = comluxgroup.createItem_1_1_0(
        {
          offerId,
          categoryId,
          productId,
          duration: 1,
          travelStart: currentItem.date,
          travelEnd: currentItem.date,
          price: offer.price,
          offerLeadPrice: offer.price,
          offerLeadDuration: '1',
          offerLeadUnit: offer.ticketUnitLabel,
          currency: state.geo.currentCurrency,
          destinationCity,
          destinationCountry,
          originCity,
          originCountry,
          itemId: currentItem.itemId,
        },
      )

      return [...items, experienceItem]
    }

    if (currentItem.itemType === 'flight') {
      const journeyId = currentItem.departing?.journeyId

      const journeysV1 = Object.values(state.flights.searchFlights).flatMap(flight => flight.journeys)
      const journeyV1 = journeysV1.find(journey => journey.id === journeyId)

      const journeysV2 = Object.values(state.flights.searchV2Flights)
        .flatMap(flight => flight.departingFares)
      const journeyv2 = journeysV2.find(journey => journey.id === journeyId)

      const firstDepartingFlightV1 = journeyV1?.departing?.flights[0]
      const firstDepartingFlightV2 = journeyv2?.flightGroup.flights[0]

      const lastDepartingFlightsV1 = journeyV1?.departing?.flights[journeyV1?.departing.flights.length - 1]
      const lastDepartingFlightsV2 = journeyv2?.flightGroup.flights[journeyv2?.flightGroup.flights.length - 1]

      const departingCost = currentItem.departing?.fareFamily?.cost.all || journeyV1?.cost || journeyv2?.price.all.totalFare
      const returningCost = currentItem.returning?.fareFamily?.cost.all
      const price = departingCost && returningCost ? departingCost + returningCost : departingCost

      const { categoryId, productId } = getCategoryAndProductId(currentItem.itemType)

      const flightItem = comluxgroup.createItem_1_1_0({
        offerId: 'travelFusion',
        categoryId,
        productId,
        travelStart: firstDepartingFlightV1?.departingDate ?? firstDepartingFlightV2?.departingDate,
        travelEnd: lastDepartingFlightsV1?.arrivalDate ?? lastDepartingFlightsV2?.arrivalDate,
        numberOfAdults: currentItem.occupants.adults,
        numberOfChildren: currentItem.occupants.children,
        numberOfInfants: currentItem.occupants.infants,
        childrenAges: currentItem.occupants.childrenAge,
        price,
        currency: state.geo.currentCurrency,
        destinationCountry: lastDepartingFlightsV1?.arrivalCountry ?? lastDepartingFlightsV2?.arrivalCountry,
        originCountry: firstDepartingFlightV1?.departingCountry ?? firstDepartingFlightV2?.departureCountry,
        itemId: currentItem.itemId,
      })

      return [...items, flightItem]
    }

    if (currentItem.itemType === 'car-hire') {
      const { categoryId, productId } = getCategoryAndProductId(currentItem.offer.parentType)

      // TODO: get addresses (destinations and origins) from currentItem.dropOff.latitute and longitude
      const carHireItem = comluxgroup.createItem_1_1_0({
        offerId: currentItem.offer.id,
        categoryId,
        productId,
        travelStart: currentItem.pickUpDate,
        travelEnd: currentItem.dropOffDate,
        duration: currentItem.offer.duration,
        price: currentItem.offer.price,
        currency: state.geo.currentCurrency,
        itemId: currentItem.itemId,
      })

      return [...items, carHireItem]
    }

    return items
  }, [])

  return filterDefinedProperties(items)
}

// Location name is a full address like 'Exmouth WA 6707, Australia' or just cityname like 'Exmouth'
function getExperienceLocations(locationName: string): { destinationCity: string, originCity: string, destinationCountry: string | null, originCountry: string | null } {
  const locations = locationName.split(' ')

  if (locations.length > 1) {
    const city = locations[0]
    const country = locations.pop() ?? null

    return {
      destinationCity: city,
      destinationCountry: country,
      originCity: city,
      originCountry: country,
    }
  } else {
    return {
      destinationCity: locationName,
      originCity: locationName,
      destinationCountry: null,
      originCountry: null,
    }
  }
}

type CategoryId = comluxgroup.Item_1_1_0['categoryId']
type ProductId = comluxgroup.Item_1_1_0['productId']

function getCategoryAndProductId(parentType: App.OfferParentType | 'flight', offer?: App.OfferBase & {source?: string}): { categoryId: CategoryId, productId: ProductId } {
  switch (parentType) {
    case 'hotel': {
      if (offer?.type === 'bedbank_hotel') return { categoryId: 'hotel', productId: 'bedbank' }
      if (offer?.type === 'last_minute_hotel') return { categoryId: 'hotel', productId: 'last_minute_hotel' }
      if (offer?.type === 'tactical_ao_hotel') return { categoryId: 'hotel', productId: 'tactical_ao_hotel' }
      if (offer?.type === 'bundle_and_save') return { categoryId: 'bundle', productId: 'bundle' }
      return { categoryId: 'hotel', productId: 'hotel' }
    }

    case 'tour_v2': return { categoryId: 'tour', productId: offer?.source === 'ttc' ? 'tourbank' : 'tour' }

    case 'tour': {
      const tourOffer = offer as App.Offer
      if (tourOffer?.holidayTypes.includes('Cruises')) return { categoryId: 'cruise', productId: 'cruise' }
      return { categoryId: 'tour', productId: 'tour' }
    }

    case 'cruise': return { categoryId: 'cruise', productId: 'cruisebank' }

    case 'experience': {
      const offerProviderAndId = offer?.id.split(':')
      let provider: string | undefined
      if (offerProviderAndId && offerProviderAndId.length > 0) provider = offerProviderAndId[0]

      const categoryId = 'experience'

      if (provider === 'led') return { categoryId, productId: 'led_exp' }
      if (provider === 'mus') return { categoryId, productId: 'mus_exp' }
      if (provider === 'rez') return { categoryId, productId: 'rez_exp' }

      return { categoryId, productId: null }
    }

    case 'car_hire': return { categoryId: 'car_hire', productId: 'car_hire' }

    case 'flight': return { categoryId: 'flight', productId: 'flight' }

    default: return { categoryId: null, productId: null }
  }
}

export async function getPageOfferCategory(state: App.State): Promise<comluxgroup.Page_1_0_0['category']> {
  if (!state.analytics.page || !state.analytics.page.currentPathParams) return

  let offerId = (state.analytics.page.currentPathParams as any).offerId
  // For some dum reason the cruises analytics page params has id and not offerId
  if (!offerId) offerId = (state.analytics.page.currentPathParams as any).id
  if (!offerId) return

  const offer = getOffer(state)
  if (!offer) return

  let hotelOrTourOffer = offer.hotelOrTourOffer
  let otherOffers = offer.otherOffers

  let category

  if (!hotelOrTourOffer && !otherOffers) {
    // offer isn't loaded yet, fetch it, this should be only in rare cases
    //   -> state should be pre-populated from SSR so offer should be present
    //   -> hence only in the case where SSR or SS data fetching is disabled + the offer isn't loaded by the time this is executed this should kick in
    //   -> we also don't want to populate the redux state from here if this is executed due to specific logic residing in hooks that load the offers that determine how the state is saved.

    // handle errors as we are making direct calls to the api, if we don't handle errors here the PageViewed event chain is stopped and not tracked
    try {
      // if offerId starts with an experience provider prefix, no current logic/selector for this
      if (Object.keys(PROVIDERS).find(provider => offerId.startsWith(provider))) {
        otherOffers = await getExperienceById(offerId, { currentRegionCode: state.geo.currentRegionCode, currentCurrency: state.geo.currentCurrency })
      } else {
        const anyOffer = await getOfferById(offerId, { region: state.geo.currentRegionCode })
        type OtherOfferTypes = Tours.V2Offer | App.CruiseOffer | App.ExperienceOffer | undefined
        // if anyOffer is of type OtherOfferTypes
        if ((anyOffer as OtherOfferTypes)?.type) {
          otherOffers = anyOffer as OtherOfferTypes
        } else {
          hotelOrTourOffer = anyOffer as App.Offer
        }
      }
    // on api failure no fallback except if it's a cruise page (getOfferById will fail for cruises v2)
    } catch {
      if (state.analytics.page.currentPath.includes('/cruises/')) {
        try {
          otherOffers = await getCruiseOffer(offerId, state.geo.currentRegionCode, state.geo.currentCurrency)
        // no remaining fallback, will return empty category
        } catch {}
      }
    }
  }

  if (hotelOrTourOffer) {
    // Handle tours that are cruises
    if (hotelOrTourOffer.type !== 'bedbank_hotel' && hotelOrTourOffer.holidayTypes?.includes('Cruises')) category = 'cruise'

    // Handle last minute hotel manually because business requirements are to track it as such
    else if (hotelOrTourOffer.type === 'last_minute_hotel') category = hotelOrTourOffer.type

    // Otherwise fallback on generic parent type
    else category = hotelOrTourOffer.parentType.includes('tour') ? 'tour' : hotelOrTourOffer.parentType
  } else if (otherOffers) {
    category = otherOffers.parentType.includes('tour') ? 'tour' : otherOffers.parentType
  }

  // Override category with ultra lux when needed, from productType string
  if (hotelOrTourOffer || otherOffers) {
    const type = hotelOrTourOffer ? hotelOrTourOffer.productType : otherOffers?.productType
    if (type?.includes('ultralux')) category = 'ultralux'
    else if (type?.includes('ultra_lux')) category = 'ultralux'
  }

  return category
}

function getOffer(state: App.State) {
  if (!state.analytics.page || !state.analytics.page.currentPathParams) return

  let offerId = (state.analytics.page.currentPathParams as any).offerId
  // For some dum reason the cruises analytics page params has id and not offerId
  if (!offerId) offerId = (state.analytics.page.currentPathParams as any).id
  if (!offerId) return

  const variationId = (state.analytics.page.currentPathParams as any).variationId

  const hotelOrTourOffer = getHotelOrTourOffer(state, offerId, variationId)
  const otherOffers = getCruiseTourV2OrExperienceOffer(state, offerId, variationId)

  return {
    hotelOrTourOffer,
    otherOffers,
    variationId,
  }
}

export function filterDefinedProperties(contexts: {
  schema: string;
  data: Record<string, unknown>;
}[]) {
  return contexts.map((context) => {
    const contextClone = { ...context }

    const data = contextClone.data
    const definedProperties = removeUndefinedAndNullProperties(data)

    return {
      ...context,
      data: definedProperties,
    }
  })
}
